import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Container, Form, ListGroup, Modal, OverlayTrigger, Popover, Spinner, Table, Tooltip } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  getGeneratedReports,
  getMyHighestRole,
  getReportDefinitions,
  getReportRuns,
  GeneratedReport,
  reRunReport,
  ReportDefinition,
  ReportRun,
  triggerReport
} from '@/api/adminApi';
import { searchUsers } from '@/services/resourcesApi';
import './AdminReports.scss';

type PropertyInputType = 'text' | 'number' | 'date' | 'datetime-local' | 'select' | 'textarea' | 'checkbox';

interface ReportPropertyDefinition {
  key: string;
  label: string;
  type: PropertyInputType;
  required: boolean;
  placeholder?: string;
  defaultValue?: string | number | boolean;
  options?: Array<{ label: string; value: string }>;
}

interface UserSearchResult {
  id?: string;
  displayName?: string;
  mail?: string;
  userPrincipalName?: string;
  email?: string;
  upn?: string;
}

interface RecipientSuggestion {
  id: string;
  displayName: string;
  email: string;
}

function safeParseJson(value?: string): unknown {
  if (!value?.trim()) return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

function formatDate(dateStr?: string) {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString();
}

function PropertyTable({ data }: { data: unknown }) {
  if (!data) return <div className="text-muted small">No data</div>;

  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    const entries = typeof parsed === 'object' && parsed !== null 
      ? Object.entries(parsed)
      : [];

    if (entries.length === 0) {
      return <div className="text-muted small">No properties</div>;
    }

    const formatValue = (val: unknown): string => {
      if (val === null || val === undefined) return '—';
      if (typeof val === 'boolean') return val ? 'Yes' : 'No';
      if (typeof val === 'object') return JSON.stringify(val);
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        return new Date(val).toLocaleString();
      }
      return String(val);
    };

    return (
      <table className="property-table">
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key}>
              <td className="property-name">{key}</td>
              <td className="property-value">{formatValue(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  } catch {
    return (
      <div className="property-error">
        <small className="text-muted">Unable to parse properties</small>
      </div>
    );
  }
}

function statusBadge(status?: string) {
  const statusLower = status?.toLowerCase() || '';
  if (statusLower === 'completed') return <Badge bg="success">Completed</Badge>;
  if (statusLower === 'failed') return <Badge bg="danger">Failed</Badge>;
  if (statusLower === 'pending') return <Badge bg="warning" text="dark">Pending</Badge>;
  if (statusLower === 'running') return <Badge bg="info">Running</Badge>;
  return <Badge bg="secondary">{status || 'N/A'}</Badge>;
}

function extractPropertyDefinitions(report: ReportDefinition | null): ReportPropertyDefinition[] {
  if (!report?.reportOptions) return [];

  const parsed = safeParseJson(report.reportOptions);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];

  const reportProperties = (parsed as Record<string, unknown>).reportProperties;
  if (!reportProperties) return [];

  if (Array.isArray(reportProperties)) {
    const mapped = reportProperties
      .map((entry, index) => {
        if (!entry || typeof entry !== 'object') return null;
        const raw = entry as Record<string, unknown>;
        const key = String(raw.key || raw.name || `field${index + 1}`).trim();
        if (!key) return null;

        return {
          key,
          label: String(raw.label || key),
          type: (raw.type as PropertyInputType) || 'text',
          required: Boolean(raw.required),
          placeholder: raw.placeholder ? String(raw.placeholder) : undefined,
          defaultValue: raw.defaultValue as string | number | boolean | undefined,
          options: Array.isArray(raw.options)
            ? raw.options
                .map((opt) => {
                  if (opt && typeof opt === 'object') {
                    const item = opt as Record<string, unknown>;
                    const value = String(item.value ?? '');
                    if (!value) return null;
                    return { label: String(item.label ?? value), value };
                  }
                  const value = String(opt ?? '');
                  return value ? { label: value, value } : null;
                })
                .filter((opt): opt is { label: string; value: string } => Boolean(opt))
            : undefined
        };
      });

    return mapped.filter((entry) => entry !== null) as ReportPropertyDefinition[];
  }

  if (typeof reportProperties === 'object') {
    return Object.entries(reportProperties as Record<string, unknown>)
      .map(([key, value]) => {
        if (value && typeof value === 'object') {
          const config = value as Record<string, unknown>;
          return {
            key,
            label: String(config.label || key),
            type: (config.type as PropertyInputType) || 'text',
            required: Boolean(config.required),
            placeholder: config.placeholder ? String(config.placeholder) : undefined,
            defaultValue: config.defaultValue as string | number | boolean | undefined,
            options: Array.isArray(config.options)
              ? config.options
                  .map((opt) => {
                    if (opt && typeof opt === 'object') {
                      const item = opt as Record<string, unknown>;
                      const optionValue = String(item.value ?? '');
                      if (!optionValue) return null;
                      return { label: String(item.label ?? optionValue), value: optionValue };
                    }
                    const optionValue = String(opt ?? '');
                    return optionValue ? { label: optionValue, value: optionValue } : null;
                  })
                  .filter((opt): opt is { label: string; value: string } => Boolean(opt))
              : undefined
          };
        }

        return {
          key,
          label: key,
          type: 'text' as PropertyInputType,
          required: false,
          defaultValue: value as string | number | boolean | undefined
        };
      });
  }

  return [];
}

function getDefaultPropertyValues(defs: ReportPropertyDefinition[]): Record<string, string | number | boolean> {
  return defs.reduce<Record<string, string | number | boolean>>((acc, def) => {
    if (typeof def.defaultValue !== 'undefined') {
      acc[def.key] = def.defaultValue;
      return acc;
    }

    if (def.type === 'checkbox') {
      acc[def.key] = false;
      return acc;
    }

    acc[def.key] = '';
    return acc;
  }, {});
}

function parseSendRunAfterSummaryEmails(runAfter?: string): string[] | null {
  if (!runAfter?.trim()) return null;
  try {
    const parsed = JSON.parse(runAfter) as Record<string, unknown>;
    if (parsed.action !== 'sendRunAfterSummary') return null;
    const email = parsed.email as Record<string, unknown> | undefined;
    const raw = typeof email?.recipientEmail === 'string' ? email.recipientEmail : '';
    const addresses = raw
      .split(/[;,]/)
      .map((e) => e.trim())
      .filter(Boolean);
    return addresses.length > 0 ? addresses : [];
  } catch {
    return null;
  }
}

function normalizePropertyKey(key: string): string {
  return key.replace(/[_\s-]/g, '').toLowerCase();
}

function isBooleanStringValue(value: unknown): value is string {
  return typeof value === 'string' && ['true', 'false'].includes(value.trim().toLowerCase());
}

function isBooleanLikeValue(value: unknown): boolean {
  return typeof value === 'boolean' || isBooleanStringValue(value);
}

function isReportManualTriggerDisabled(report: ReportDefinition): boolean {
  const settings = safeParseJson(report.reportSettings);
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) return false;
  const val = (settings as Record<string, unknown>).manualTriggerDisabled;
  return val === true || String(val).toLowerCase() === 'true';
}

function toNumericId(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function isSameReportId(a: unknown, b: unknown): boolean {
  const left = toNumericId(a);
  const right = toNumericId(b);
  return left !== null && right !== null && left === right;
}

export function AdminReports() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username ?? '';

  const [reports, setReports] = useState<ReportDefinition[]>([]);
  const [runs, setRuns] = useState<ReportRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [runsLoading, setRunsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showCreateRunModal, setShowCreateRunModal] = useState(false);
  const [openingCreateRunModal, setOpeningCreateRunModal] = useState(false);
  const [createRunBusy, setCreateRunBusy] = useState(false);
  const [selectedReportIdForRun, setSelectedReportIdForRun] = useState<number | null>(null);
  const [propertyValues, setPropertyValues] = useState<Record<string, string | number | boolean>>({});
  // null = runAfter does not require summary emails; string[] = email list (may be empty)
  const [runAfterSummaryEmails, setRunAfterSummaryEmails] = useState<string[] | null>(null);
  const [runAfterRecipients, setRunAfterRecipients] = useState<string[]>([]);
  const [runAfterSearchTerm, setRunAfterSearchTerm] = useState('');
  const [recipientSuggestions, setRecipientSuggestions] = useState<RecipientSuggestion[]>([]);
  const [searchingRecipients, setSearchingRecipients] = useState(false);
  const [generatedReportsByRunId, setGeneratedReportsByRunId] = useState<Record<number, GeneratedReport[]>>({});
  const [loadingGeneratedForRunIds, setLoadingGeneratedForRunIds] = useState<Set<number>>(new Set());

  const [reRunningRunId, setReRunningRunId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null);
  const [expandingRunId, setExpandingRunId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  const filterReportId = useMemo(() => {
    const value = Number(searchParams.get('reportId'));
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }, [searchParams]);

  const selectedRunReport = useMemo(
    () => reports.find((r) => isSameReportId(r.m365ReportId, selectedReportIdForRun)) ?? null,
    [reports, selectedReportIdForRun]
  );

  const selectedReportIsLocked = useMemo(() => {
    const report = reports.find((r) => isSameReportId(r.m365ReportId, selectedReportIdForRun));
    return Boolean(report && isReportManualTriggerDisabled(report));
  }, [reports, selectedReportIdForRun]);

  const selectedReportIsRestricted = useMemo(
    () => Boolean(selectedReportIsLocked && !isSuperAdmin),
    [selectedReportIsLocked, isSuperAdmin]
  );

  const reportPropertyDefinitions = useMemo(
    () => extractPropertyDefinitions(selectedRunReport),
    [selectedRunReport]
  );

  const debugProperty = useMemo(
    () => reportPropertyDefinitions.find((property) => normalizePropertyKey(property.key) === 'debug') ?? null,
    [reportPropertyDefinitions]
  );

  const debugLevelProperty = useMemo(
    () => reportPropertyDefinitions.find((property) => normalizePropertyKey(property.key) === 'debuglevel') ?? null,
    [reportPropertyDefinitions]
  );

  const regularPropertyDefinitions = useMemo(
    () => reportPropertyDefinitions.filter((property) => {
      const normalized = normalizePropertyKey(property.key);
      return normalized !== 'debug' && normalized !== 'debuglevel';
    }),
    [reportPropertyDefinitions]
  );

  const testProperty = useMemo(
    () => regularPropertyDefinitions.find((property) => normalizePropertyKey(property.key) === 'test') ?? null,
    [regularPropertyDefinitions]
  );

  const nonPinnedPropertyDefinitions = useMemo(
    () => regularPropertyDefinitions.filter((property) => normalizePropertyKey(property.key) !== 'test'),
    [regularPropertyDefinitions]
  );

  function isStringBooleanProperty(property: ReportPropertyDefinition): boolean {
    if (property.type === 'checkbox') return false;

    const currentValue = propertyValues[property.key];
    if (isBooleanLikeValue(currentValue) || isBooleanLikeValue(property.defaultValue)) {
      return true;
    }

    const optionValues = (property.options || [])
      .map((option) => String(option.value ?? '').trim().toLowerCase())
      .filter(Boolean);

    return optionValues.length > 0 && optionValues.every((value) => value === 'true' || value === 'false');
  }

  function getSwitchChecked(property: ReportPropertyDefinition): boolean {
    if (property.type === 'checkbox') {
      return Boolean(propertyValues[property.key]);
    }

    const currentValue = propertyValues[property.key];
    const normalized = String(
      typeof currentValue === 'undefined' ? property.defaultValue ?? 'false' : currentValue
    )
      .trim()
      .toLowerCase();

    return normalized === 'true';
  }

  function updatePropertyFromSwitch(property: ReportPropertyDefinition, checked: boolean) {
    setPropertyValues((prev) => ({
      ...prev,
      [property.key]: property.type === 'checkbox' ? checked : checked ? 'true' : 'false'
    }));
  }

  function isBooleanUiProperty(property: ReportPropertyDefinition): boolean {
    return property.type === 'checkbox' || isStringBooleanProperty(property);
  }

  const booleanProperties = useMemo(
    () => nonPinnedPropertyDefinitions.filter((property) => isBooleanUiProperty(property)),
    [nonPinnedPropertyDefinitions, propertyValues]
  );

  const nonBooleanProperties = useMemo(
    () => nonPinnedPropertyDefinitions.filter((property) => !isBooleanUiProperty(property)),
    [nonPinnedPropertyDefinitions, propertyValues]
  );

  const requiresSummaryEmails = runAfterSummaryEmails !== null;

  const filteredRuns = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return runs;
    return runs.filter((run) =>
      String(run.m365ReportRunId).includes(q) ||
      (run.reportTitle ?? '').toLowerCase().includes(q) ||
      (run.reportStatus ?? '').toLowerCase().includes(q)
    );
  }, [runs, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredRuns.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedRuns = useMemo(
    () => filteredRuns.slice(startIndex, endIndex),
    [filteredRuns, startIndex, endIndex]
  );

  useEffect(() => {
    void initialLoad();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterReportId]);

  useEffect(() => {
    if (!loading) {
      void loadRuns(filterReportId);
    }
  }, [filterReportId, loading]);

  useEffect(() => {
    const defaults = getDefaultPropertyValues(reportPropertyDefinitions);
    setPropertyValues(defaults);
  }, [reportPropertyDefinitions]);

  useEffect(() => {
    if (!showCreateRunModal || !requiresSummaryEmails) {
      setRecipientSuggestions([]);
      setSearchingRecipients(false);
      return;
    }

    const term = runAfterSearchTerm.trim();
    if (term.length < 2) {
      setRecipientSuggestions([]);
      setSearchingRecipients(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setSearchingRecipients(true);
        const results = await searchUsers(term);
        if (cancelled) return;

        const currentRecipients = new Set(
          runAfterRecipients.map((email) => email.trim().toLowerCase())
        );

        const suggestions = (results as UserSearchResult[])
          .map((user, index) => {
            const email = (user.mail || user.userPrincipalName || user.email || user.upn || '').trim();
            if (!email) return null;
            return {
              id: user.id || `${email}-${index}`,
              displayName: (user.displayName || email).trim(),
              email
            };
          })
          .filter((entry): entry is RecipientSuggestion => Boolean(entry))
          .filter((entry) => !currentRecipients.has(entry.email.toLowerCase()))
          .slice(0, 8);

        setRecipientSuggestions(suggestions);
      } catch {
        if (!cancelled) setRecipientSuggestions([]);
      } finally {
        if (!cancelled) setSearchingRecipients(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [runAfterRecipients, runAfterSearchTerm, requiresSummaryEmails, showCreateRunModal]);

  async function initialLoad() {
    try {
      setLoading(true);
      setError(null);
      const [reportData, runData, roleData] = await Promise.all([
        getReportDefinitions(),
        getReportRuns(filterReportId),
        getMyHighestRole()
      ]);
      setReports(reportData);
      setRuns(runData);
      setIsSuperAdmin(String(roleData?.role || '').trim().toLowerCase() === 'superadmin');

      if (filterReportId && !reportData.some((r) => r.m365ReportId === filterReportId)) {
        const next = new URLSearchParams(searchParams);
        next.delete('reportId');
        setSearchParams(next, { replace: true });
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to load reports data');
    } finally {
      setLoading(false);
    }
  }

  async function loadRuns(m365ReportId?: number) {
    try {
      setRunsLoading(true);
      const data = await getReportRuns(m365ReportId);
      setRuns(data);
      setGeneratedReportsByRunId({});

      const candidateRuns = data.filter((run) => run.generatedReportsCount > 0);
      if (candidateRuns.length > 0) {
        const loadingSet = new Set(candidateRuns.map((run) => run.m365ReportRunId));
        setLoadingGeneratedForRunIds(loadingSet);

        const results = await Promise.allSettled(
          candidateRuns.map(async (run) => {
            const generated = await getGeneratedReports(run.m365ReportRunId);
            return { runId: run.m365ReportRunId, generated };
          })
        );

        const byRunId: Record<number, GeneratedReport[]> = {};
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            byRunId[result.value.runId] = result.value.generated;
          }
        });

        setGeneratedReportsByRunId(byRunId);
      }

      setLoadingGeneratedForRunIds(new Set());
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to load report runs');
    } finally {
      setRunsLoading(false);
    }
  }

  function renderGeneratedIcon(run: ReportRun) {
    if (run.generatedReportsCount <= 0) {
      return <span className="text-muted">-</span>;
    }

    if (loadingGeneratedForRunIds.has(run.m365ReportRunId)) {
      return <Spinner animation="border" size="sm" />;
    }

    const generated = generatedReportsByRunId[run.m365ReportRunId] || [];
    if (generated.length === 0) {
      return <span className="text-muted">-</span>;
    }

    if (generated.length === 1) {
      const onlyFile = generated[0];
      const title = onlyFile.reportFilename || 'Open generated report';
      return (
        <a
          href={onlyFile.reportLink}
          target="_blank"
          rel="noopener noreferrer"
          title={title}
          aria-label={title}
          className="report-file-link-icon"
        >
          <i className="fas fa-file-lines" />
        </a>
      );
    }

    const popover = (
      <Popover id={`generated-files-popover-${run.m365ReportRunId}`} className="generated-files-popover">
        <Popover.Header as="h3">Generated Files ({generated.length})</Popover.Header>
        <Popover.Body className="p-0">
          <ListGroup variant="flush">
            {generated.map((file) => (
              <ListGroup.Item
                key={file.m365AdminReportId}
                as="a"
                href={file.reportLink}
                target="_blank"
                rel="noopener noreferrer"
                action
                className="small"
              >
                <div className="fw-semibold text-truncate">{file.reportFilename || 'Generated report'}</div>
                <div className="text-muted">{formatDate(file.createDt)}</div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Popover.Body>
      </Popover>
    );

    return (
      <OverlayTrigger trigger="click" rootClose placement="left" overlay={popover}>
        <button type="button" className="report-file-link-icon icon-button" aria-label={`View ${generated.length} generated files`}>
          <i className="fas fa-file-lines" />
          <sup className="ms-1">{generated.length}</sup>
        </button>
      </OverlayTrigger>
    );
  }

  function openCreateRunModal() {
    if (openingCreateRunModal) return;

    // Give immediate UI feedback before rendering the modal/form content.
    setOpeningCreateRunModal(true);
    window.requestAnimationFrame(() => {
      setSelectedReportIdForRun(null);
      setRunAfterSummaryEmails(null);
      setRunAfterRecipients([]);
      setRunAfterSearchTerm('');
      setRecipientSuggestions([]);
      setShowCreateRunModal(true);
      setOpeningCreateRunModal(false);
    });
  }

  function handleCreateRunReportChange(reportId: number | null) {
    setSelectedReportIdForRun(reportId);
    const report = reports.find((r) => isSameReportId(r.m365ReportId, reportId)) ?? null;
    const emails = parseSendRunAfterSummaryEmails(report?.runAfter);
    setRunAfterSummaryEmails(emails);
    setRunAfterRecipients(emails !== null && currentUserEmail ? [currentUserEmail] : []);
    setRunAfterSearchTerm('');
    setRecipientSuggestions([]);
  }

  function handleSelectRecipientSuggestion(suggestion: RecipientSuggestion) {
    setRunAfterRecipients((prev) => {
      const normalized = suggestion.email.trim().toLowerCase();
      if (prev.some((email) => email.trim().toLowerCase() === normalized)) return prev;
      return [...prev, suggestion.email.trim()];
    });
    setRunAfterSearchTerm('');
    setRecipientSuggestions([]);
  }

  function handleRemoveRecipient(emailToRemove: string) {
    const removeNormalized = emailToRemove.trim().toLowerCase();
    setRunAfterRecipients((prev) => prev.filter((email) => email.trim().toLowerCase() !== removeNormalized));
  }

  function handleRecipientSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    if (recipientSuggestions.length > 0) {
      handleSelectRecipientSuggestion(recipientSuggestions[0]);
    }
  }

  function canSearchRecipients() {
    return Boolean(selectedReportIdForRun && requiresSummaryEmails);
  }

  function isRecipientSelected(email: string) {
    const normalized = email.trim().toLowerCase();
    return runAfterRecipients.some((recipient) => recipient.trim().toLowerCase() === normalized);
  }

  function getRecipientInvalidState() {
    return requiresSummaryEmails && runAfterRecipients.length === 0;
  }

  function getRecipientTooltipText() {
    return 'This report sends a summary email after running. Separate multiple addresses with semicolons. You can remove yourself, but at least one recipient is required.';
  }

  function getRecipientSearchPlaceholder() {
    return canSearchRecipients() ? 'Type a name or email to search users...' : 'Select a report first';
  }

  function getRecipientSearchHelpText() {
    return 'Separate multiple addresses with semicolons.';
  }

  function getRecipientAddresses() {
    return runAfterRecipients.map((e) => e.trim()).filter(Boolean);
  }

  function getRecipientAddressString() {
    return getRecipientAddresses().join(';');
  }

  function getRecipientSuggestionsToDisplay() {
    return recipientSuggestions.filter((suggestion) => !isRecipientSelected(suggestion.email));
  }

  function shouldShowRecipientSuggestions() {
    return !searchingRecipients && getRecipientSuggestionsToDisplay().length > 0;
  }

  function getRecipientSuggestionsItems() {
    return getRecipientSuggestionsToDisplay();
  }

  function getRecipientSearchValue() {
    return runAfterSearchTerm;
  }

  function setRecipientSearchValue(value: string) {
    setRunAfterSearchTerm(value);
  }

  async function handleCreateRun() {
    if (!selectedReportIdForRun) return;

    const report = reports.find((r) => isSameReportId(r.m365ReportId, selectedReportIdForRun)) || null;
    if (!report) {
      setError('Selected report definition was not found. Please re-select the report.');
      return;
    }

    if (isReportManualTriggerDisabled(report) && !isSuperAdmin) {
      setError('This report can only be manually triggered by a Super Admin.');
      return;
    }

    if (requiresSummaryEmails) {
      const addresses = getRecipientAddresses();
      if (addresses.length === 0) {
        setError('At least one recipient email is required for this report.');
        return;
      }
    }

    try {
      setCreateRunBusy(true);
      setError(null);

      const baseOptions = safeParseJson(report?.reportOptions) || {};
      const optionsPayload: Record<string, unknown> =
        typeof baseOptions === 'object' && !Array.isArray(baseOptions)
          ? { ...(baseOptions as Record<string, unknown>) }
          : { rawReportOptions: report?.reportOptions || '' };

      // If runAfter requires a summary email, store the full runAfter object with updated recipients
      if (requiresSummaryEmails) {
        const addresses = getRecipientAddressString();
        const runAfterParsed = safeParseJson(report?.runAfter) as Record<string, unknown> | undefined;
        optionsPayload.runAfter = {
          ...(runAfterParsed ?? {}),
          email: {
            ...((runAfterParsed?.email ?? {}) as object),
            recipientEmail: addresses
          }
        };
      }

      await triggerReport(
        selectedReportIdForRun,
        JSON.stringify(optionsPayload),
        JSON.stringify(propertyValues)
      );

      setShowCreateRunModal(false);
      await loadRuns(filterReportId);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to create report run');
    } finally {
      setCreateRunBusy(false);
    }
  }

  async function handleReRun(runId: number) {
    try {
      setReRunningRunId(runId);
      await reRunReport(runId);
      await loadRuns(filterReportId);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to re-run report');
    } finally {
      setReRunningRunId(null);
    }
  }

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center p-4">
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="admin-reports mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h2 className="mb-0">M365 Report Runs</h2>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={() => navigate('/admin/reports/catalog')}>
            View M365 Reports
          </Button>
          <Button
            variant="success"
            className="text-white add-run-btn"
            onClick={openCreateRunModal}
            disabled={openingCreateRunModal}
          >
            {openingCreateRunModal && <Spinner animation="border" size="sm" className="me-2 loading-spinner-inline" />}
            {openingCreateRunModal ? 'Opening...' : 'Add Report Run'}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <strong>{filterReportId ? `Runs for Report #${filterReportId}` : 'All Report Runs'}</strong>
          <div className="d-flex gap-2 flex-wrap">
            <Form.Control
              size="sm"
              type="search"
              placeholder="Search runs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ minWidth: '160px' }}
            />
            <Form.Select
              size="sm"
              value={filterReportId || ''}
              onChange={(e) => {
                const value = Number(e.target.value);
                const next = new URLSearchParams(searchParams);
                if (Number.isFinite(value) && value > 0) {
                  next.set('reportId', String(value));
                } else {
                  next.delete('reportId');
                }
                setSearchParams(next, { replace: true });
              }}
            >
              <option value="">All Reports</option>
              {reports.map((report) => (
                <option key={report.m365ReportId} value={report.m365ReportId}>
                  {report.reportTitle}
                </option>
              ))}
            </Form.Select>
            <Button size="sm" variant="outline-primary" onClick={() => loadRuns(filterReportId)} disabled={runsLoading}>
              Refresh
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {runsLoading ? (
            <div className="text-center p-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : filteredRuns.length === 0 ? (
            <div className="text-muted p-4">{runs.length === 0 ? 'No report runs found.' : 'No runs match your search.'}</div>
          ) : (
            <>
            <Table hover responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Run ID</th>
                  <th>Report</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Generated</th>
                  <th>File</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRuns.map((run) => {
                  const isExpanded = expandedRunId === run.m365ReportRunId;
                  const isExpanding = expandingRunId === run.m365ReportRunId;
                  const hasDetails = run.reportProperties || run.reportResult;
                  return (
                    <>
                      <tr
                        key={run.m365ReportRunId}
                        className={isExpanded ? 'table-active' : ''}
                        style={{ cursor: hasDetails ? 'pointer' : undefined }}
                        onClick={() => {
                          if (hasDetails) {
                            if (isExpanded) {
                              setExpandedRunId(null);
                              setExpandingRunId(null);
                            } else {
                              setExpandingRunId(run.m365ReportRunId);
                              setExpandedRunId(null);
                              // Show loading immediately, then content after brief delay
                              setTimeout(() => {
                                setExpandedRunId(run.m365ReportRunId);
                                setExpandingRunId(null);
                              }, 100);
                            }
                          }
                        }}
                      >
                        <td>{run.m365ReportRunId}</td>
                        <td>{run.reportTitle}</td>
                        <td>{statusBadge(run.reportStatus)}</td>
                        <td>{run.reportComplete} / {run.reportRun}</td>
                        <td>{formatDate(run.reportStartDate)}</td>
                        <td>{formatDate(run.reportEndDate)}</td>
                        <td>{run.generatedReportsCount}</td>
                        <td>{renderGeneratedIcon(run)}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReRun(run.m365ReportRunId);
                            }}
                            disabled={reRunningRunId === run.m365ReportRunId}
                          >
                            {reRunningRunId === run.m365ReportRunId ? 'Re-running...' : 'Re-run'}
                          </Button>
                        </td>
                      </tr>
                      {isExpanding && (
                        <tr key={`${run.m365ReportRunId}-loading`}>
                          <td colSpan={9}>
                            <div className="admin-report-detail admin-report-loading">
                              <Spinner animation="border" size="sm" className="me-2" />
                              <span className="text-muted">Loading details...</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {isExpanded && hasDetails && (
                        <tr key={`${run.m365ReportRunId}-detail`} className="admin-report-detail-row">
                          <td colSpan={9}>
                            <div className="admin-report-detail">
                              <div className="admin-report-details-container">
                                {run.reportProperties && (
                                  <div className="admin-report-section">
                                    <h6 className="admin-report-section-title">Report Properties</h6>
                                    <PropertyTable data={run.reportProperties} />
                                  </div>
                                )}
                                {run.reportResult && (
                                  <div className="admin-report-section">
                                    <h6 className="admin-report-section-title">Report Result</h6>
                                    <PropertyTable data={run.reportResult} />
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </Table>
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
              <div className="text-muted small">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredRuns.length)} of {filteredRuns.length}
              </div>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ← Previous
                </Button>
                <div className="d-flex align-items-center px-2">
                  <span className="small">
                    Page {currentPage} of {totalPages}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </Button>
              </div>
            </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showCreateRunModal} onHide={() => setShowCreateRunModal(false)} dialogClassName="create-run-modal" size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Add Report Run</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Report</Form.Label>
            <Form.Select
              value={selectedReportIdForRun || ''}
              onChange={(e) => handleCreateRunReportChange(Number(e.target.value) || null)}
            >
              <option value="">Select a report</option>
              {reports.map((report) => {
                const restricted = isReportManualTriggerDisabled(report);
                return (
                  <option
                    key={report.m365ReportId}
                    value={report.m365ReportId}
                    style={restricted ? { color: '#6c757d' } : undefined}
                  >
                    {restricted ? '🔒 ' : ''}{report.reportTitle}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>

          {selectedReportIsRestricted && (
            <Alert variant="warning" className="d-flex align-items-start gap-2 mt-2">
              <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>🔒</span>
              <div>
                <strong>Access Denied</strong>
                <div className="mt-1 small">
                  Please contact the primary administrator to run this report.
                </div>
              </div>
            </Alert>
          )}

          {selectedReportIsLocked && isSuperAdmin && (
            <Alert variant="warning" className="d-flex align-items-start gap-2 mt-2 border border-warning-subtle bg-warning-subtle">
              <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>⚠️</span>
              <div>
                <strong>Locked report: Super Admin override</strong>
                <div className="mt-1 small">
                  This report is locked, but current user is Super Admin. Please be careful when running this report.
                </div>
              </div>
            </Alert>
          )}

          {!selectedReportIsRestricted && requiresSummaryEmails && (
            <Form.Group className="mb-3">
              <Form.Label>
                Recipient Emails <span className="text-danger">*</span>
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement="right"
                  overlay={
                    <Tooltip id="recipient-emails-info-tooltip">
                      {getRecipientTooltipText()}
                    </Tooltip>
                  }
                >
                  <span
                    className="recipient-info-icon ms-2"
                    aria-label="Recipient email instructions"
                    title={getRecipientTooltipText()}
                    role="button"
                    tabIndex={0}
                  >
                    i
                  </span>
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                type="search"
                value={getRecipientSearchValue()}
                onChange={(e) => setRecipientSearchValue(e.target.value)}
                onKeyDown={handleRecipientSearchKeyDown}
                placeholder={getRecipientSearchPlaceholder()}
                disabled={!canSearchRecipients()}
                isInvalid={getRecipientInvalidState()}
              />
                {searchingRecipients && (
                  <div className="small text-muted mt-2">Searching users...</div>
                )}
              <div className="recipient-suggestions-container">
                {shouldShowRecipientSuggestions() && (
                  <ListGroup className="recipient-suggestions-dropdown">
                    {getRecipientSuggestionsItems().map((suggestion) => (
                      <ListGroup.Item
                        key={suggestion.id}
                        action
                        onClick={() => handleSelectRecipientSuggestion(suggestion)}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span>{suggestion.displayName}</span>
                        <span className="text-muted small">{suggestion.email}</span>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </div>
              {runAfterRecipients.length > 0 && (
                <div className="recipient-pill-list mt-2">
                  {runAfterRecipients.map((email) => (
                    <span key={email} className="recipient-pill">
                      <span className="recipient-pill-email">{email}</span>
                      <button
                        type="button"
                        className="recipient-pill-remove"
                        onClick={() => handleRemoveRecipient(email)}
                        aria-label={`Remove ${email}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <Form.Text className="text-muted">
                  {getRecipientSearchHelpText()}
              </Form.Text>
              <Form.Control.Feedback type="invalid">
                At least one recipient email is required.
              </Form.Control.Feedback>
            </Form.Group>
          )}

          {reportPropertyDefinitions.length > 0 ? (
            <fieldset disabled={selectedReportIsRestricted} style={selectedReportIsRestricted ? { opacity: 0.6 } : undefined}>
                <div className="report-settings-top-divider border-top mb-3" />
                <div className="report-settings-header">
                  Report settings
                  {selectedReportIsRestricted && <span className="text-muted fw-normal small ms-2">(view only)</span>}
                </div>
                <hr className="report-settings-divider mt-0 mb-3" />

              {testProperty && (
                <Form.Group key={testProperty.key} className="mb-3">
                  {isBooleanUiProperty(testProperty) ? (
                    <Form.Check
                      type="switch"
                      id={`run-property-${testProperty.key}`}
                      checked={getSwitchChecked(testProperty)}
                      onChange={(e) => updatePropertyFromSwitch(testProperty, e.target.checked)}
                      label={`${testProperty.label}${testProperty.required ? ' *' : ''}`}
                    />
                  ) : (
                    <>
                      <Form.Label>
                        {testProperty.label}
                        {testProperty.required ? ' *' : ''}
                      </Form.Label>

                      {testProperty.type === 'select' ? (
                        <Form.Select
                          value={String(propertyValues[testProperty.key] ?? '')}
                          onChange={(e) =>
                            setPropertyValues((prev) => ({
                              ...prev,
                              [testProperty.key]: e.target.value
                            }))
                          }
                        >
                          <option value="">Select...</option>
                          {(testProperty.options || []).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      ) : testProperty.type === 'textarea' ? (
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={String(propertyValues[testProperty.key] ?? '')}
                          placeholder={testProperty.placeholder}
                          onChange={(e) =>
                            setPropertyValues((prev) => ({
                              ...prev,
                              [testProperty.key]: e.target.value
                            }))
                          }
                        />
                      ) : (
                        <Form.Control
                          type={testProperty.type}
                          value={String(propertyValues[testProperty.key] ?? '')}
                          placeholder={testProperty.placeholder}
                          onChange={(e) =>
                            setPropertyValues((prev) => ({
                              ...prev,
                              [testProperty.key]: testProperty.type === 'number' ? Number(e.target.value) : e.target.value
                            }))
                          }
                        />
                      )}
                    </>
                  )}
                </Form.Group>
              )}

              {nonBooleanProperties.map((property) => (
                <Form.Group key={property.key} className="mb-3">
                  <Form.Label>
                    {property.label}
                    {property.required ? ' *' : ''}
                  </Form.Label>

                  {property.type === 'select' ? (
                    <Form.Select
                      value={String(propertyValues[property.key] ?? '')}
                      onChange={(e) =>
                        setPropertyValues((prev) => ({
                          ...prev,
                          [property.key]: e.target.value
                        }))
                      }
                    >
                      <option value="">Select...</option>
                      {(property.options || []).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  ) : property.type === 'textarea' ? (
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={String(propertyValues[property.key] ?? '')}
                      placeholder={property.placeholder}
                      onChange={(e) =>
                        setPropertyValues((prev) => ({
                          ...prev,
                          [property.key]: e.target.value
                        }))
                      }
                    />
                  ) : (
                    <Form.Control
                      type={property.type}
                      value={String(propertyValues[property.key] ?? '')}
                      placeholder={property.placeholder}
                      onChange={(e) =>
                        setPropertyValues((prev) => ({
                          ...prev,
                          [property.key]: property.type === 'number' ? Number(e.target.value) : e.target.value
                        }))
                      }
                    />
                  )}
                </Form.Group>
              ))}

              {booleanProperties.length > 0 && (
                <div className="row g-3 mb-3">
                  {booleanProperties.map((property) => (
                    <div className="col-12 col-md-6 col-xl-4" key={property.key}>
                      <Form.Check
                        type="switch"
                        id={`run-property-${property.key}`}
                        checked={getSwitchChecked(property)}
                        onChange={(e) => updatePropertyFromSwitch(property, e.target.checked)}
                        label={`${property.label}${property.required ? ' *' : ''}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              {(debugProperty || debugLevelProperty) && (
                <div className="run-debug-settings row g-3 border-top align-items-center">
                  {debugProperty && (
                    <div className="col-12 col-md-6 col-xl-4">
                      <Form.Check
                        className="debug-switch-inline"
                        type="switch"
                        id={`run-property-${debugProperty.key}`}
                        checked={getSwitchChecked(debugProperty)}
                        onChange={(e) => updatePropertyFromSwitch(debugProperty, e.target.checked)}
                        label={debugProperty.label}
                      />
                    </div>
                  )}

                  {debugLevelProperty && (
                    <div className="col-12 col-md-6 col-xl-4">
                      <Form.Group className="mb-0 debug-level-inline">
                        <Form.Label className="mb-0">{debugLevelProperty.label}</Form.Label>
                        <Form.Select
                          value={String(propertyValues[debugLevelProperty.key] ?? '')}
                          onChange={(e) =>
                            setPropertyValues((prev) => ({
                              ...prev,
                              [debugLevelProperty.key]: Number(e.target.value)
                            }))
                          }
                        >
                          {Array.from({ length: 10 }, (_, value) => (
                            <option key={value} value={value}>
                              {value}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>
                  )}
                </div>
              )}
            </fieldset>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateRunModal(false)} disabled={createRunBusy}>
            Cancel
          </Button>
          <Button variant="success" className="text-white" onClick={handleCreateRun} disabled={createRunBusy || !selectedReportIdForRun || selectedReportIsRestricted}>
            {createRunBusy ? 'Creating...' : 'Create Run'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
